/* eslint-disable @typescript-eslint/no-explicit-any,react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import useUploadForm from 'hooks/useUploadForm.ts';
import useStyles from './DropFileZone.styles.ts';
import { Box, LinearProgress, useTheme } from '@mui/material';
import { Button, Typo } from 'components/primitive';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { DMAFileTypes } from 'interfaces/ia.interface.ts';
import { FileContainer } from 'components/fragment';
import { bytesToSize, getProperKeyNameDummySearchString } from 'utils/index.ts';
import { listFilesRequest, uploadFileRequest } from 'utils/FileUploadAPI.ts';

interface FileType extends DMAFileTypes, File {
  name: string;
  size: number;
}

type DropFileZoneProps = {
  readOnly: boolean;
  authToken: string;
  uuid: string;
  pvDataID?: number;
  hasOldData?: boolean;
  files?: DMAFileTypes[];
  filesQueuedCallback: React.Dispatch<React.SetStateAction<any>>;
  filesDeletedCallback: React.Dispatch<React.SetStateAction<any>>;
};

const DropFileZone: React.FC<DropFileZoneProps> = ({ hasOldData = false, ...props }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const baseStyle = classes.baseStyle;
  const focusedStyle = classes.focusedStyle;
  const acceptStyle = classes.acceptStyle;
  const rejectStyle = classes.rejectStyle;

  const {
    uploadForm,
    downloadForm,
    uploadedDocuments,
    progress,
    inProgress,
    setUploadedDocuments,
  } = useUploadForm<FileType>(hasOldData);
  const [files, setFiles] = useState<FileType[]>([]);
  const [filesDeleted, setFilesDeleted] = useState<FileType[]>([]);
  const [uploadError, setUploadError] = useState(false);
  const [listError, setListError] = useState(false);

  useEffect(() => {
    // *: Get the uploaded files
    const fetchData = async () => {
      const { url, config } = listFilesRequest(props.uuid, props.authToken);
      return await downloadForm(url, config, props.files || []);
    };
    if (props.uuid && props.authToken && !inProgress) {
      setListError(false);
      fetchData().catch((error) => {
        console.log('🚀 fetchData error: ', error);
        setListError(true);
      });
    }
  }, [hasOldData, props.authToken, props.uuid, props.files]);

  const removeFile = (fileToRemove: FileType) => {
    const arr = uploadedDocuments;
    const fileToDelete: any[] | FileType[] = [];
    for (let i = 0; i < arr.length; i++) {
      if (arr[i].pvDataID === fileToRemove.pvDataID) {
        fileToDelete.push(arr[i]);
        arr.splice(i, 1);
      }
    }
    setUploadedDocuments((prevFiles) =>
      prevFiles.filter((file) => file.pvDataID !== fileToRemove.pvDataID)
    );
    const arr2 = [...filesDeleted, ...fileToDelete];
    setFilesDeleted(arr2);
    props.filesDeletedCallback && props.filesDeletedCallback(() => arr2);
  };

  const onDrop = useCallback(async (acceptedFiles: FileType[] | any[]) => {
    // *: A new attempt clears the last failure, so the error shows only while it is true.
    setUploadError(false);
    setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
    const uploadFiles = [...files, ...acceptedFiles];
    // noinspection TypeScriptValidateJSTypes
    props.filesQueuedCallback && props.filesQueuedCallback(uploadFiles);
    const tempUploadedFiles: FileType[] = [];
    try {
      for (let i = 0; i < uploadFiles.length; i++) {
        const file = uploadFiles[i];
        const { url, config } = uploadFileRequest(props.authToken);

        const uploadedFile = await uploadForm<FileType>(file, url, config);
        console.log('🚀  uploadedFile: ', uploadedFile);
        // *: Any 2xx counts: which success status the Files API returns is
        // *: unverified, and a 201 must not drop the file silently.
        // *: A body with neither name field would white-screen FileContainer's
        // *: `.toLocaleLowerCase()` on render, so it is treated as a failed upload.
        const hasUsableName =
          typeof uploadedFile.data?.cbrnDataFileName === 'string' ||
          typeof uploadedFile.data?.file_name === 'string';
        if (uploadedFile.status >= 200 && uploadedFile.status < 300 && hasUsableName) {
          tempUploadedFiles.push(uploadedFile.data);
        } else if (uploadedFile.status >= 200 && uploadedFile.status < 300) {
          console.error('🚀 onDrop upload response had no usable file name: ', {
            status: uploadedFile.status,
          });
          setUploadError(true);
        }
      }
      setUploadedDocuments((prevFiles) => [...prevFiles, ...tempUploadedFiles]);
      // noinspection TypeScriptValidateJSTypes
      props.filesQueuedCallback && props.filesQueuedCallback(tempUploadedFiles);
    } catch (error) {
      console.log('🚀onDrop Upload error: ', error);
      setUploadError(true);
    }
  }, []);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject, open } = useDropzone({
    onDrop,
    // Disable click and keydown behavior
    noClick: true,
    noKeyboard: true,
  });

  const style: React.CSSProperties | any = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [baseStyle, isFocused, focusedStyle, isDragAccept, acceptStyle, isDragReject, rejectStyle]
  );

  if (!props.uuid) return null;

  return (
    <>
      {!inProgress && Array.isArray(uploadedDocuments) && uploadedDocuments.length > 0 ? (
        <Box sx={classes.btnContainer} onClick={open}>
          Add File
          <AddCircleIcon fontSize='small' />
        </Box>
      ) : null}
      {inProgress ? (
        <Box sx={{ width: '100%' }}>
          <LinearProgress variant='determinate' value={progress} />
        </Box>
      ) : null}
      {!inProgress && listError && (
        <Box sx={classes.errorContainer}>
          <Typo role='alert' sx={classes.errorText}>
            Unable to load existing documents
          </Typo>
        </Box>
      )}
      <>
        {!inProgress && Array.isArray(uploadedDocuments) && uploadedDocuments.length > 0
          ? uploadedDocuments.map((file: FileType, idx: number) => {
              if (!file.pvVoid || file.deleted === 0 || file.deleted === null) {
                const fileName = Object.prototype.hasOwnProperty.call(file, 'cbrnDataFileName')
                  ? (file.cbrnDataFileName ?? '')
                  : Object.prototype.hasOwnProperty.call(file, 'file_name')
                    ? (file.file_name ?? '')
                    : '';
                const hasDummyName: boolean = fileName?.indexOf('_DUMMYSEARCHSTRING_') >= 0;
                const name: string = hasDummyName
                  ? getProperKeyNameDummySearchString(fileName ?? 'FILE NAME')
                  : (fileName ?? 'FILE NAME');
                const byteSize = Object.prototype.hasOwnProperty.call(file, 'cbrnDataFileSize')
                  ? bytesToSize(file?.cbrnDataFileSize ?? 0)
                  : bytesToSize(file?.file_size ?? 0);
                return (
                  <FileContainer
                    readOnly={props.readOnly}
                    key={file?.pvDataID || idx}
                    authToken={props.authToken}
                    name={name}
                    item={file}
                    bytes={byteSize}
                    removeFile={(item) => removeFile(item)}
                  />
                );
              }
              return <></>;
            })
          : null}
      </>
      {!inProgress && uploadedDocuments.length === 0 && (
        <div {...getRootProps({ style })}>
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ color: '#C2C2C2', fontSize: '36px' }} />
          <Typo sx={classes.fileUploadTitle}>Drag & drop a file to upload</Typo>
          <Typo sx={classes.fileUploadSubTitle}>Or</Typo>
          <Button onClick={open}>BROWSE</Button>
        </div>
      )}
      {!inProgress && uploadError && (
        <Box sx={classes.errorContainer}>
          <Typo role='alert' sx={classes.errorText}>
            Error uploading file
          </Typo>
        </Box>
      )}
    </>
  );
};

export default DropFileZone;
